import React, { useState } from 'react';
import { Button, ButtonGroup, Modal, PageLoader } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { useAuthState } from '@vis-auth/react';
import ExternalLinks from '../../constants/ExternalLinks';
import { useEnvironment } from '../../context/Environment/EnvironmentContext';
import { useLoginDotGovVisAuthService } from '../../services/VisAuthService';
import loginDotGovImage from '../../images/loginDotGov.png';
import './AccountMigrationModal.scss';
import AuthScopes from '../../constants/AuthScopes';

const AccountMigrationModal = (): JSX.Element => {
  const { accessToken, user } = useAuthState();
  const { LOGIN_DOT_GOV_AUTH_CLIENTID } = useEnvironment();
  const visAuthService = useLoginDotGovVisAuthService();

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [loadingMigration, setLoadingMigration] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  if (!document.getElementById('root')) {
    const root = document.createElement('div');
    root.id = 'root';
    document.body.appendChild(root);
    Modal.setAppElement('#root');
  }

  const onMigrateClick: () => void = async () => {
    if (accessToken && LOGIN_DOT_GOV_AUTH_CLIENTID && !loadingMigration) {
      try {
        setIsOpen(false);
        setLoadingMigration(true);
        if (user?.user_id) {
          localStorage.setItem('MFAMigrationComplete', user.user_id);
        }
        await visAuthService.authorizeMigrateRedirect(
          accessToken,
          LOGIN_DOT_GOV_AUTH_CLIENTID,
          AuthScopes.LOGIN_DOT_GOV,
        );
      } catch (e) {
        setErrorMessage('An error occurred with our system. Please try again.');
        setIsOpen(true);
        setLoadingMigration(false);
      }
    } else {
      setErrorMessage('An error occurred with our system. Please try again.');
    }
  };

  return (
    <>
      {loadingMigration && <PageLoader label="loading" />}
      <Modal
        id="account-migration-modal"
        header="Migrate your account to Login.gov"
        open={isOpen}
        close={(): void => setIsOpen(false)}
      >
        <Modal.Content>
          <Alert status="info" slim className="margin-bottom-105">
            <Alert.Text className="grid-col-10">
              All users must migrate to Login.gov by <b>June 30, 2025</b>.
            </Alert.Text>
          </Alert>
          <img src={loginDotGovImage} width="100%" alt="Login.gov Logo" className="display-block margin-bottom-3" />
          <p>
            <b>Login.gov is a sign in service</b> that offers secure and private access to{' '}
            <span className="nobr">E-Verify</span>. You must have a Login.gov account and enable Multi-Factor
            Authentication (MFA) to continue your access to <span className="nobr">E-Verify</span>.
          </p>
          {errorMessage && (
            <Alert status="error" slim className="margin-top-4">
              <Alert.Text>{errorMessage}</Alert.Text>
            </Alert>
          )}
        </Modal.Content>
        <Modal.Footer className="account-migration-modal-footer">
          <ButtonGroup>
            <a
              className="usa-button usa-button--outline"
              aria-label="Click here to learn more about account migration to Login.gov in a new tab"
              target="_blank"
              href={`${ExternalLinks.MFA_MIGRATION}`}
              rel="noopener noreferrer"
            >
              Learn More
            </a>
            <Button onClick={onMigrateClick}>Migrate Your Account</Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountMigrationModal;
